import SyncController from "../APIController/SyncController"

const SyncHelper = () => {
    const { fetchSyncData, ping, uploadDTO } = SyncController()

    const sync = async () => {
        if (await pingRequest() === false) return;
        await sendDataToServer()
        await getDataFromServer()
        
    }

    const sendDataToServer = async() =>{
        const toSyncDataStr = localStorage.getItem("toSyncData");
        if (!toSyncDataStr) return;

        const toSyncData = JSON.parse(toSyncDataStr);
        const orders = toSyncData.orders || [];

        if (orders.length === 0) return;

        const cleanedOrders = orders.map(({ productList, ...keep }) => keep);
        const payload = { orders: cleanedOrders };

        try {
            const response = await uploadDTO(payload);
            if (response?.ok) {
                localStorage.removeItem("toSyncData");
            }
        } catch (err) {
            console.error("Upload fehlgeschlagen:", err);
        }
    }

    const getDataFromServer = async() =>{
        const data = await fetchSyncData()
        localStorage.setItem(
            "objectData",
            JSON.stringify(data)
        );
    }

    const getObjectData = () =>{
        return localStorage.getItem("objectData");
    }

    const pingRequest = async (timeoutMs = 1000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
            const response = await ping({ signal: controller.signal });
            clearTimeout(timeoutId);
            return response?.ok || false;
        } catch {
            return false;
        }
    }

    const readOffline = (category) => {
        const syncData = getObjectData()

        if (!syncData) return {};

        try {
            const data = JSON.parse(syncData);
            return data[category] || {};
        } catch (error) {
            console.error("No Data Error:", error);
            return {};
        }
    }

    const readToSync = (category) => {
        const toSyncData = localStorage.getItem("toSyncData");

        if (!toSyncData) return {};
        try {
            const data = JSON.parse(toSyncData);
            return data[category] || {};
        }
        catch (error) {
            console.error("No Data Error:", error);
            return {};
        }
    }

    const readOfflineOrdersComplete = (category) => {
        const syncData = readOffline(category);
        const toSyncData = readToSync("orders");

        const orderStatus = category === "activeOrders" ? "PENDING" : "COMPLETE";

        const filteredToSync = toSyncData.filter(order => order.orderStatus === orderStatus);
        return [...syncData, ...filteredToSync];
    }

    const readOfflineProducts = (categoryId) => {
        const syncData = getObjectData()

        if (!syncData) return [];

        try {
            const data = JSON.parse(syncData);
            const products = data.products || [];

            return products.filter(product =>
                product.productStatus === 'ACTIVE' &&
                product.category.id === categoryId
            );
        } catch (error) {
            console.error("No Data Error:", error);
            return [];
        }
    }

    const allOfflineOrders = () => {
        const syncData = readOffline("activeOrders");
        const syncDataInactive = readOffline("inactiveOrders");
        const toSyncData = readToSync("orders");
        return [...syncData, ...syncDataInactive, ...toSyncData];
    }

    const calculateOrderPrice = (orderDTO) => {
        const syncData = getObjectData()
        if (!syncData) return 0;

        try {
            const data = JSON.parse(syncData);
            const allProducts = data.products || [];
            const allExtras = data.extras || [];
            const allDiscounts = data.discounts || [];

            let totalPrice = 0;
            const products = orderDTO.orderProducts || orderDTO.productList || [];

            let discount = null;
            if (orderDTO.discountId) {
                discount = allDiscounts.find(d => d.id === orderDTO.discountId && d.productStatus === 'ACTIVE');
            }

            for (const orderProduct of products) {
                const product = allProducts.find(p => p.id === orderProduct.productId);
                if (!product) continue;

                let productPrice = product.price;

                if (product.reduceable && discount) {
                    const discountAmount = productPrice / 100 * discount.percentage;
                    productPrice = Math.round((productPrice - discountAmount) * 100) / 100;
                }

                const extraIds = orderProduct.extras || orderProduct.extraIds || [];
                for (const extraId of extraIds) {
                    const extra = allExtras.find(e => e.id === extraId && e.productStatus === 'ACTIVE');
                    if (extra) {
                        productPrice += extra.price;
                    }
                }

                productPrice *= orderProduct.count;

                totalPrice += productPrice;
            }

            return totalPrice;

        } catch (error) {
            console.error("Fehler bei Preisberechnung:", error);
            return 0;
        }
    }

    const offlineOrderCreation = (orderDTO) => {
        try {
            const syncData = getObjectData()
            const data = JSON.parse(syncData);
            const allProducts = data.products || [];
            const allExtras = data.extras || [];

            const totalPrice = calculateOrderPrice(orderDTO);

            const productList = orderDTO.orderProducts.map(op => {
                const product = allProducts.find(p => p.id === op.productId);
                const extras = op.extras.map(extraId =>
                    allExtras.find(e => e.id === extraId)
                ).filter(Boolean);

                return {
                    count: op.count,
                    productDTO: {
                        name: product?.name || 'Unbekannt',
                        price: product?.price || 0,
                        categoryId: product?.category?.id,
                        reduceable: product?.reduceable || false,
                        ean: product?.ean
                    },
                    extraDTOList: extras.map(e => ({
                        name: e.name,
                        price: e.price
                    }))
                };
            });

            const offlineId = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const order = {
                ...orderDTO,
                id: offlineId,
                productList: productList,
                totalPrice: totalPrice,
                orderStatus: orderDTO.orderStatus || "PENDING",
                startTime: new Date().toISOString().slice(0, -1)
            };

            const toSyncData = localStorage.getItem("toSyncData");
            let syncList = { orders: [] };

            if (toSyncData) {
                syncList = JSON.parse(toSyncData);
                if (!syncList.orders) {
                    syncList.orders = [];
                }
            }

            syncList.orders.push(order);
            localStorage.setItem("toSyncData", JSON.stringify(syncList));

            return order;

        } catch (error) {
            console.error("Fehler bei offline Order Creation:", error);
            return null;
        }
    }

    const updateOrderStatus = (id, status) => {
        const toSyncData = localStorage.getItem("toSyncData");
        if (!toSyncData) return false;

        try {
            const data = JSON.parse(toSyncData);
            const orders = data.orders || [];
            const orderIndex = orders.findIndex(o => o.id === id);
            if (orderIndex === -1) return false;

            const order = orders[orderIndex];
            order.orderStatus = status;
            if (status === "COMPLETE") {
                order.endTime = new Date().toISOString().slice(0, -1);

                if (order.startTime) {
                    const start = new Date(order.startTime);
                    const end = new Date(order.endTime);
                    const diffMs = end - start;
                    order.duration = msToIsoDuration(diffMs);
                }
            }

            localStorage.setItem("toSyncData", JSON.stringify(data));
            return true;

        } catch (error) {
            console.error("Fehler beim Aktualisieren des Bestellstatus:", error);
            return false;
        }
    }

    function msToIsoDuration(ms) {
        let seconds = ms / 1000;
        const hours = Math.floor(seconds / 3600);
        seconds -= hours * 3600;

        const minutes = Math.floor(seconds / 60);
        seconds -= minutes * 60;
        const secondsStr = seconds.toFixed(9).replace(/0+$/, "");

        return `PT${hours > 0 ? hours + "H" : ""}${minutes > 0 ? minutes + "M" : ""}${secondsStr}S`;
    }

    const removeSyncData = () => {
        localStorage.removeItem("objectData");
    }

    return {
        sync,
        removeSyncData,
        pingRequest,
        readOffline,
        readOfflineProducts,
        calculateOrderPrice,
        offlineOrderCreation,
        readOfflineOrdersComplete,
        allOfflineOrders,
        updateOrderStatus
    }
}

export default SyncHelper